import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { articles } from '../../mocks/articles';
import AnimatedSection from '../../components/base/AnimatedSection';

const categories = [
  {
    id: 'ai',
    name: 'Artificial Intelligence',
    description: 'Machine learning, deep learning, neural networks, and AI applications transforming industries.',
    icon: 'ri-robot-line',
    color: 'from-cyan-500 to-teal-500',
    image: 'https://public.readdy.ai/ai/img_res/55661f4c8efd072cad21a90a6752f373.jpg',
  },
  {
    id: 'blockchain',
    name: 'Blockchain',
    description: 'Distributed ledger technology, smart contracts, DeFi, and decentralized applications.',
    icon: 'ri-links-line',
    color: 'from-amber-500 to-orange-500',
    image: 'https://readdy.ai/api/search-image?query=abstract%20blockchain%20network%20visualization%20with%20interconnected%20golden%20blocks%20glowing%20chain%20links%20distributed%20ledger%20nodes%20luminous%20cryptographic%20patterns%20in%20warm%20amber%20and%20orange%20tones%20against%20dark%20background%20with%20depth%20layers&width=400&height=250&seq=cat-blockchain-001&orientation=landscape',
  },
  {
    id: 'quantum',
    name: 'Quantum Computing',
    description: 'Quantum mechanics, qubits, quantum algorithms, and the future of computation.',
    icon: 'ri-atom-line',
    color: 'from-cyan-500 to-teal-500',
    image: 'https://readdy.ai/api/search-image?query=quantum%20computing%20visualization%20with%20spinning%20qubits%20entangled%20particles%20wave%20functions%20superposition%20states%20glowing%20atomic%20structures%20in%20cyan%20and%20teal%20colors%20against%20deep%20space%20background%20with%20particle%20effects%20and%20light%20trails&width=400&height=250&seq=cat-quantum-001&orientation=landscape',
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    description: 'Threat detection, encryption, zero trust architecture, and security best practices.',
    icon: 'ri-shield-check-line',
    color: 'from-rose-500 to-pink-500',
    image: 'https://readdy.ai/api/search-image?query=cybersecurity%20digital%20fortress%20visualization%20with%20protective%20shields%20encryption%20locks%20firewall%20barriers%20glowing%20security%20nodes%20in%20rose%20and%20pink%20tones%20against%20dark%20matrix%20background%20with%20binary%20code%20streams&width=400&height=250&seq=cat-security-001&orientation=landscape',
  },
  {
    id: 'iot',
    name: 'IoT & Edge Computing',
    description: 'Connected devices, edge processing, smart systems, and industrial IoT solutions.',
    icon: 'ri-cpu-line',
    color: 'from-emerald-500 to-green-500',
    image: 'https://readdy.ai/api/search-image?query=internet%20of%20things%20network%20visualization%20with%20connected%20smart%20devices%20sensors%20processors%20glowing%20data%20pathways%20in%20emerald%20and%20green%20colors%20against%20dark%20background%20with%20circuit%20patterns%20and%20edge%20computing%20nodes&width=400&height=250&seq=cat-iot-001&orientation=landscape',
  },
  {
    id: 'ar-vr',
    name: 'AR/VR & Metaverse',
    description: 'Augmented reality, virtual reality, spatial computing, and immersive experiences.',
    icon: 'ri-glasses-line',
    color: 'from-cyan-500 to-teal-500',
    image: 'https://readdy.ai/api/search-image?query=virtual%20reality%20metaverse%20visualization%20with%20immersive%20digital%20worlds%20holographic%20interfaces%20floating%20UI%20elements%20glowing%20portals%20in%20cyan%20and%20teal%20colors%20against%20dark%20space%20with%20depth%20layers%20and%20volumetric%20effects&width=400&height=250&seq=cat-arvr-001&orientation=landscape',
  },
];

export default function CategoriesPage() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const getArticleCount = (categoryId: string) => {
    return articles.filter(a => a.category === categoryId).length;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative pt-20">
        <div className="absolute inset-0 z-0 h-80">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20technology%20categories%20visualization%20with%20multiple%20glowing%20hexagonal%20portals%20interconnected%20topic%20nodes%20floating%20category%20icons%20luminous%20pathways%20in%20cyan%20teal%20and%20electric%20blue%20colors%20against%20deep%20dark%20gradient%20with%20particle%20effects%20depth%20layers%20volumetric%20lighting&width=1920&height=600&seq=categories-hero-001&orientation=landscape"
            alt="Categories Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-cyan-400/30 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <i className="ri-folder-line text-cyan-400"></i>
              <span className="text-sm font-medium">Browse Topics</span>
            </div>
            <h1 className={`text-5xl font-extrabold text-white leading-tight mb-5 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Explore Categories
            </h1>
            <p className={`text-xl text-white/90 leading-relaxed transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Browse articles by topic. Find the latest insights in your area of interest.
            </p>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <AnimatedSection key={category.id} animation="fadeUp" delay={index * 100}>
              <Link
                to={`/articles?category=${category.id}`}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-500 cursor-pointer flex flex-col hover:-translate-y-2"
              >
                <div className="w-full h-40 overflow-hidden relative">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed flex-grow">
                    {category.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-sm text-gray-500 font-medium">
                      {getArticleCount(category.id)} articles
                    </span>
                    <span className="text-cyan-600 text-sm font-medium group-hover:translate-x-2 transition-transform inline-flex items-center gap-1">
                      Explore <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
